/**
 * Pole of inaccessibility: the interior point farthest from the polygon outline, i.e. the
 * centre of the largest inscribed circle. Used to place land-use labels so they sit well
 * inside L-shaped or concave cells (a centroid can fall outside or hug an edge).
 * Grid refinement with a priority queue (same idea as Mapbox "polylabel", own implementation).
 * Input: polygon = [outer ring, ...holes], rings as [[x, y], ...] in a planar metric system.
 * Returns [x, y, distance] (distance in the input unit; 0 for degenerate input).
 */

function segDist2(px, py, a, b) {
  let x = a[0], y = a[1], dx = b[0] - x, dy = b[1] - y;
  if (dx !== 0 || dy !== 0) {
    const t = ((px - x) * dx + (py - y) * dy) / (dx * dx + dy * dy);
    if (t > 1) { x = b[0]; y = b[1]; } else if (t > 0) { x += dx * t; y += dy * t; }
  }
  dx = px - x; dy = py - y;
  return dx * dx + dy * dy;
}

/** signed distance from a point to the polygon outline (positive inside) */
function pointToPolygon(x, y, polygon) {
  let inside = false, min = Infinity;
  for (const ring of polygon) {
    for (let i = 0, n = ring.length, j = n - 1; i < n; j = i++) {
      const a = ring[i], b = ring[j];
      if ((a[1] > y) !== (b[1] > y) && x < ((b[0] - a[0]) * (y - a[1])) / (b[1] - a[1]) + a[0]) inside = !inside;
      min = Math.min(min, segDist2(x, y, a, b));
    }
  }
  return (inside ? 1 : -1) * Math.sqrt(min);
}

function cell(x, y, h, polygon) {
  const d = pointToPolygon(x, y, polygon);
  return { x, y, h, d, max: d + h * Math.SQRT2 };
}

/** binary max-heap on `max` */
class Heap {
  constructor() { this.a = []; }
  get size() { return this.a.length; }
  push(v) {
    const a = this.a; a.push(v);
    let i = a.length - 1;
    while (i > 0) { const p = (i - 1) >> 1; if (a[p].max >= v.max) break; a[i] = a[p]; i = p; }
    a[i] = v;
  }
  pop() {
    const a = this.a, top = a[0], last = a.pop();
    if (a.length) {
      let i = 0;
      for (;;) {
        const l = 2 * i + 1, r = l + 1;
        let m = i;
        let mv = last.max;
        if (l < a.length && a[l].max > mv) { m = l; mv = a[l].max; }
        if (r < a.length && a[r].max > mv) m = r;
        if (m === i) break;
        a[i] = a[m]; i = m;
      }
      a[i] = last;
    }
    return top;
  }
}

function centroidCell(polygon) {
  const ring = polygon[0];
  let area = 0, x = 0, y = 0;
  for (let i = 0, n = ring.length, j = n - 1; i < n; j = i++) {
    const a = ring[i], b = ring[j], f = a[0] * b[1] - b[0] * a[1];
    x += (a[0] + b[0]) * f; y += (a[1] + b[1]) * f; area += f * 3;
  }
  return area === 0 ? cell(ring[0][0], ring[0][1], 0, polygon) : cell(x / area, y / area, 0, polygon);
}

export function polylabel(polygon, precision = 0.5) {
  const ring = polygon?.[0];
  if (!ring || ring.length < 3) return ring?.length ? [ring[0][0], ring[0][1], 0] : [0, 0, 0];
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of ring) { if (p[0] < minX) minX = p[0]; if (p[1] < minY) minY = p[1]; if (p[0] > maxX) maxX = p[0]; if (p[1] > maxY) maxY = p[1]; }
  const w = maxX - minX, h = maxY - minY, size = Math.min(w, h);
  if (size === 0) return [minX, minY, 0];
  let half = size / 2;
  const q = new Heap();
  for (let x = minX; x < maxX; x += size) for (let y = minY; y < maxY; y += size) q.push(cell(x + half, y + half, half, polygon));
  let best = centroidCell(polygon);
  const bboxCell = cell(minX + w / 2, minY + h / 2, 0, polygon);
  if (bboxCell.d > best.d) best = bboxCell;
  let guard = 0;
  while (q.size && guard++ < 20000) {
    const c = q.pop();
    if (c.d > best.d) best = c;
    if (c.max - best.d <= precision) continue;
    half = c.h / 2;
    q.push(cell(c.x - half, c.y - half, half, polygon));
    q.push(cell(c.x + half, c.y - half, half, polygon));
    q.push(cell(c.x - half, c.y + half, half, polygon));
    q.push(cell(c.x + half, c.y + half, half, polygon));
  }
  return [best.x, best.y, Math.max(0, best.d)];
}
